import { AddIcon } from '@chakra-ui/icons';
import {
    Box,
    ButtonProps,
    Checkbox,
    Divider,
    Drawer,
    DrawerContent,
    Flex,
    IconButton,
    Tooltip,
    useColorMode,
    useDisclosure,
    VStack,
} from '@chakra-ui/react';
import { cloneDeep, flatten } from 'lodash';
import isEqual from 'lodash/isEqual';
import React, { useEffect, useMemo, useState } from 'react';
import { useCurrentColors } from '../../../hooks/UseCurrentColors/UseCurrentColors';
import CanopyButton from '../../CanopyButton/CanopyButton';
import CanopySelect from '../../CanopySelect/CanopySelect';
import DrawerHeader from '../../Drawer/CustomDrawerHeader';
import ActClose from '../../Icons/ActClose';
import ActFilter from '../../Icons/ActFilter';
type OnApply = (filter: string[][], groupBy: string[]) => void;

export interface CanopyFiltersProps extends ButtonProps {
    globalProps: GlobalProps;
    groupByFilters?: Record<string, Record<string, string>[]>;
    selectedGroupByFilters?: string[][];
    groupBy?: string[];
    selectedGroupBy?: string[];
    allowMultipleFilters?: boolean;
    onApply?: OnApply;
}

function Filters({
    globalProps,
    groupByFilters = {},
    selectedGroupByFilters = [],
    groupBy = [],
    selectedGroupBy = [''],
    allowMultipleFilters = true,
    onApply,
}: CanopyFiltersProps) {
    const [localSelectedGroupBy, setLocalSelectedGroupBy] = useState<string[]>([
        'select-one',
    ]);
    const [localSelectedGroupByFilters, setLocalSelectedGroupByFilters] =
        useState<string[][]>([]);
    const [currentFilters, setCurrentFilters] = useState<
        Record<string, string>[][]
    >([]);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [currentSelectedIndex, setCurrentSelectedIndex] =
        useState<number>(-1);
    const [resetTriggered, setResetTriggered] = useState(false);
    const currentColors = useCurrentColors();
    const { colorMode } = useColorMode();
    const { t } = globalProps;

    const updateFilters = () => {
        setCurrentSelectedIndex(-1);
        setResetTriggered(false);
        selectedGroupBy &&
            !isEqual(localSelectedGroupBy, selectedGroupBy) &&
            setLocalSelectedGroupBy(selectedGroupBy);
        selectedGroupBy?.forEach((item, index) => {
            if (selectedGroupByFilters[index]?.length > 0) {
                let filters = groupByFilters?.[item] && [
                    ...groupByFilters[item],
                ];
                const flattenArr =
                    selectedGroupBy[0] !== item
                        ? flatten(
                              localSelectedGroupByFilters.length
                                  ? localSelectedGroupByFilters
                                  : selectedGroupByFilters
                          )
                        : [];
                if (
                    flattenArr.length &&
                    selectedGroupBy.length === 2 &&
                    selectedGroupBy[index] === item
                ) {
                    filters = filters.filter((filItem) => {
                        // for child-username
                        let key = 'parentUsernames';
                        if (item !== 'child-username') {
                            key = 'childArr';
                        }
                        return flattenArr.some((arrItem: string) =>
                            filItem[key].includes(arrItem)
                        );
                    });
                }
                setCurrentFilters((currentFilters) => {
                    currentFilters.splice(index, 1, [...filters]);
                    return currentFilters;
                });
            }
        });
        if (selectedGroupBy.length > 1) {
            setCurrentSelectedIndex(selectedGroupBy.length - 1);
        }
        !isEqual(localSelectedGroupByFilters, selectedGroupByFilters) &&
            setLocalSelectedGroupByFilters([...selectedGroupByFilters]);
    };
    useEffect(() => {
        updateFilters();
    }, [selectedGroupBy, selectedGroupByFilters]);

    useEffect(() => {
        selectedGroupBy &&
            !isEqual(localSelectedGroupBy, selectedGroupBy) &&
            setLocalSelectedGroupBy(selectedGroupBy);
    }, [selectedGroupBy]);

    useEffect(() => {
        selectedGroupByFilters &&
            !isEqual(localSelectedGroupByFilters, selectedGroupByFilters) &&
            setLocalSelectedGroupByFilters([...selectedGroupByFilters]);
    }, [selectedGroupByFilters]);

    const handleGroupByChange = (item: any, index: number) => {
        //ts-ignore
        const _localSelectedGroupBy = cloneDeep(localSelectedGroupBy);
        const _currentFilters = cloneDeep(currentFilters);
        const _localSelectedGroupByFilters = cloneDeep(
            localSelectedGroupByFilters
        );
        _localSelectedGroupBy?.splice(index, 1, item.value);
        let filters = groupByFilters?.[item?.value] && [
            ...groupByFilters[item.value],
        ];
        const flattenArr =
            _localSelectedGroupBy[0] !== item.value
                ? flatten(localSelectedGroupByFilters)
                : [];
        if (
            flattenArr.length &&
            _localSelectedGroupBy.length === 2 &&
            _localSelectedGroupBy[index] === item.value
        ) {
            filters = filters.filter((filItem) => {
                // for child-username
                let key = 'parentUsernames';
                if (item.value !== 'child-username') {
                    key = 'childArr';
                }
                return flattenArr.some((arrItem: string) =>
                    filItem[key].includes(arrItem)
                );
            });
        }
        _currentFilters?.splice(index, 1, [...filters]);
        _localSelectedGroupByFilters?.splice(index, 1, []);
        // have to set the currentFilters of the _localSelectedGroupByFilters users
        setLocalSelectedGroupBy(_localSelectedGroupBy);
        setCurrentFilters(_currentFilters);
        setLocalSelectedGroupByFilters(_localSelectedGroupByFilters);
        if (localSelectedGroupBy.length > 1) {
            setCurrentSelectedIndex(localSelectedGroupBy.length - 1);
        }
        if (
            _localSelectedGroupBy.length === 2 &&
            _localSelectedGroupBy[0] === item.value
        ) {
            setLocalSelectedGroupByFilters([]);
            setLocalSelectedGroupBy([_localSelectedGroupBy[0]]);
        }
    };

    const handleFilterChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        index: number
    ) => {
        const _localSelectedGroupByFilters = cloneDeep(
            localSelectedGroupByFilters
        );
        const filters = _localSelectedGroupByFilters[index];
        if (event.target.checked) {
            !filters.includes(event.target.value) &&
                filters.push(event.target.value);
        } else {
            filters.includes(event.target.value) &&
                filters.splice(filters.indexOf(event.target.value), 1);
        }
        _localSelectedGroupByFilters.splice(index, 1, filters);
        setLocalSelectedGroupByFilters(_localSelectedGroupByFilters);
    };

    const handleSelectAll = (
        event: React.ChangeEvent<HTMLInputElement>,
        index: number
    ) => {
        const filters = event.target.checked
            ? currentFilters[index].map(({ value }) => String(value))
            : [];
        const _localSelectedGroupByFilters = cloneDeep(
            localSelectedGroupByFilters
        );
        _localSelectedGroupByFilters.splice(index, 1, filters);
        setLocalSelectedGroupByFilters(_localSelectedGroupByFilters);
    };

    const handleResetFilter = () => {
        setLocalSelectedGroupBy(['select-one']);
        setLocalSelectedGroupByFilters([]);
        setCurrentSelectedIndex(-1);
        setResetTriggered(true);
    };

    const handleCloseFilter = () => {
        const _localSelectedGroupByFilters = cloneDeep(
            localSelectedGroupByFilters
        );
        setLocalSelectedGroupBy([localSelectedGroupBy[0]]);
        setLocalSelectedGroupByFilters(_localSelectedGroupByFilters);
        setCurrentSelectedIndex(-1);
    };

    const handleApplyFilter = () => {
        onClose();
        setResetTriggered(false);
        if (onApply) {
            localSelectedGroupByFilters &&
                localSelectedGroupBy &&
                onApply(localSelectedGroupByFilters, localSelectedGroupBy);
        }
    };

    const handleAddFilter = () => {
        const _localSelectedGroupBy = cloneDeep(localSelectedGroupBy);
        _localSelectedGroupBy?.push('select-one');
        setLocalSelectedGroupBy(_localSelectedGroupBy);
        setCurrentSelectedIndex(_localSelectedGroupBy.length - 1);
    };

    const isDisabledApplyButton = () => {
        if (resetTriggered) {
            return false;
        }

        return (
            (isEqual(selectedGroupByFilters, localSelectedGroupByFilters) &&
                isEqual(selectedGroupBy, localSelectedGroupBy)) ||
            localSelectedGroupByFilters.findIndex(
                (item) => item.length === 0
            ) !== -1 ||
            localSelectedGroupBy.includes('select-one') ||
            localSelectedGroupByFilters.length === 0
        );
    };

    const isDisabledAddButton = useMemo(
        () => () =>
            localSelectedGroupBy?.length >= groupBy?.length ||
            localSelectedGroupBy.includes('select-one') ||
            (localSelectedGroupBy?.length !== 0 &&
                localSelectedGroupBy.findIndex((item) => item.length === 0) !==
                    -1),
        [localSelectedGroupBy]
    );

    const getOptions = (
        groupBy: string[],
        localSelectedGroupBy: string[],
        index: number
    ) => {
        const options = groupBy?.reduce((acc: any, item: string) => {
            if (
                localSelectedGroupBy[index] === item ||
                !localSelectedGroupBy.includes(item)
            ) {
                acc?.push({
                    value: item,
                    label: t(`ui.mm.filter.${item}`),
                });
            }
            return acc;
        }, []);
        return options;
    };

    return (
        <>
            <Tooltip
                aria-label="page-filters"
                label={t('ui.mm.tooltip.filters')}
                placement="bottom-start"
                hasArrow>
                <IconButton
                    background="transparent"
                    cursor="pointer"
                    border="none"
                    borderRadius="4px"
                    aria-label="page-filters"
                    width="20px !important"
                    minWidth="20px !important"
                    height="20px !important"
                    onClick={() => {
                        updateFilters();
                        onOpen();
                    }}
                    icon={<ActFilter />}
                    backgroundColor="transparent"
                    color={currentColors.brand['300']}
                    variant="ghost"
                    _hover={{ backgroundColor: 'transparent' }}
                    _focus={{
                        border: '0px',
                        backgroundColor: 'transparent',
                        outline: 'none',
                    }}
                    _active={{ border: '0px' }}
                />
            </Tooltip>
            <Drawer
                isOpen={isOpen}
                placement="right"
                onClose={onClose}
                size="sm">
                <DrawerContent
                    px="17px"
                    py="17px"
                    height={{ xl: '100vh', sm: 'calc(100% - 10px)' }}
                    backgroundColor={currentColors.componentBg}>
                    <Flex direction="column">
                        <DrawerHeader
                            onClick={onClose}
                            header={t('ui.mm.filter-categories')}
                            logoHeader={t('ui.mm.page-filter')}></DrawerHeader>
                    </Flex>
                    <Flex
                        overflowY="auto"
                        direction="column"
                        marginBottom="30px"
                        justify="space-between">
                        {localSelectedGroupBy?.map((item, index) => {
                            return (
                                <Flex
                                    key={index}
                                    data-test="filter-area"
                                    direction="column"
                                    flexGrow={1}
                                    mt="15px"
                                    mb="15px">
                                    <Flex>
                                        <CanopySelect
                                            id={`filters${index}`}
                                            placeholder={t('select')}
                                            value={{
                                                value: localSelectedGroupBy[
                                                    index
                                                ],
                                                label: t(
                                                    `ui.mm.filter.${localSelectedGroupBy[index]}`
                                                ),
                                            }}
                                            options={getOptions(
                                                groupBy,
                                                localSelectedGroupBy,
                                                index
                                            )}
                                            isDisabled={
                                                currentSelectedIndex > index
                                            }
                                            onChange={(
                                                value: React.ChangeEvent<HTMLInputElement>
                                            ) => {
                                                handleGroupByChange(
                                                    value,
                                                    index
                                                );
                                            }}
                                            height="35px"></CanopySelect>
                                        {index > 0 && (
                                            <IconButton
                                                background="transparent"
                                                cursor="pointer"
                                                border="none"
                                                ml="5px"
                                                display="inline-flex"
                                                onClick={handleCloseFilter}
                                                color={
                                                    currentColors.brand['500']
                                                }
                                                width="24px"
                                                minWidth="24px"
                                                height="24px"
                                                aria-label="remove"
                                                icon={<ActClose />}
                                                variant="ghost"
                                                alignSelf="flex-start"
                                                mt="8px"
                                                _hover={{
                                                    backgroundColor:
                                                        'transparent',
                                                }}
                                                _focus={{
                                                    border: '0px',
                                                    backgroundColor:
                                                        'transparent',
                                                    outline: 'none',
                                                }}
                                                _active={{ border: '0px' }}
                                            />
                                        )}
                                    </Flex>
                                    <Flex
                                        direction="column"
                                        justify="space-between"
                                        flexGrow={1}>
                                        {' '}
                                        <Flex
                                            direction="column"
                                            flexGrow={1}>
                                            <Flex
                                                mt="15px"
                                                maxHeight={
                                                    allowMultipleFilters
                                                        ? '200px'
                                                        : 'none'
                                                }
                                                backgroundColor={
                                                    currentColors.brand['900']
                                                }
                                                borderRadius="4px"
                                                p="10px"
                                                overflowY="auto"
                                                width="100%">
                                                {currentFilters?.[0]?.length ? (
                                                    <Flex
                                                        direction="column"
                                                        width="100%">
                                                        {item !==
                                                        'select-one' ? (
                                                            <>
                                                                <Checkbox
                                                                    colorScheme={colorMode+'Primary'}
                                                                    key={
                                                                        'select-all'
                                                                    }
                                                                    isChecked={
                                                                        localSelectedGroupByFilters?.[
                                                                            index
                                                                        ]
                                                                            ?.length ===
                                                                        currentFilters?.[
                                                                            index
                                                                        ]
                                                                            ?.length
                                                                    }
                                                                    isDisabled={
                                                                        currentSelectedIndex >
                                                                        index
                                                                    }
                                                                    isIndeterminate={
                                                                        localSelectedGroupByFilters?.[
                                                                            index
                                                                        ]
                                                                            ?.length !==
                                                                            currentFilters?.[
                                                                                index
                                                                            ]
                                                                                ?.length &&
                                                                        localSelectedGroupByFilters?.[
                                                                            index
                                                                        ]
                                                                            ?.length !==
                                                                            0
                                                                    }
                                                                    fontWeight={
                                                                        localSelectedGroupByFilters?.[
                                                                            index
                                                                        ]
                                                                            ?.length ===
                                                                        currentFilters?.length
                                                                            ? '600'
                                                                            : 'normal'
                                                                    }
                                                                    height="18px"
                                                                    mb="15px"
                                                                    onChange={(
                                                                        value: any
                                                                    ) => {
                                                                        handleSelectAll(
                                                                            value,
                                                                            index
                                                                        );
                                                                    }}>
                                                                    {t(
                                                                        'select-all'
                                                                    )}
                                                                </Checkbox>

                                                                <VStack
                                                                    width="100%"
                                                                    align="flex-start"
                                                                    fontSize="15px">
                                                                    {currentFilters?.[
                                                                        index
                                                                    ]?.map(
                                                                        (
                                                                            filterItem: Record<
                                                                                string,
                                                                                string
                                                                            >
                                                                        ) => {
                                                                            return (
                                                                                <Checkbox
                                                                                    width="100%"
                                                                                    colorScheme={colorMode+'Primary'}
                                                                                    key={String(
                                                                                        filterItem.value
                                                                                    )}
                                                                                    value={String(
                                                                                        filterItem.value
                                                                                    )}
                                                                                    onChange={(
                                                                                        event: React.ChangeEvent<HTMLInputElement>
                                                                                    ) =>
                                                                                        handleFilterChange(
                                                                                            event,
                                                                                            index
                                                                                        )
                                                                                    }
                                                                                    isDisabled={
                                                                                        currentSelectedIndex >
                                                                                        index
                                                                                    }
                                                                                    isChecked={localSelectedGroupByFilters[
                                                                                        index
                                                                                    ]?.includes(
                                                                                        String(
                                                                                            filterItem.value
                                                                                        )
                                                                                    )}
                                                                                    fontWeight={
                                                                                        localSelectedGroupByFilters?.[
                                                                                            index
                                                                                        ]?.find(
                                                                                            (
                                                                                                key
                                                                                            ) => {
                                                                                                return (
                                                                                                    key ===
                                                                                                    String(
                                                                                                        filterItem.value
                                                                                                    )
                                                                                                );
                                                                                            }
                                                                                        )
                                                                                            ? '600'
                                                                                            : 'normal'
                                                                                    }
                                                                                    height="30px">
                                                                                    <Box
                                                                                        width="250px"
                                                                                        textOverflow="ellipsis"
                                                                                        overflow="hidden"
                                                                                        whiteSpace="nowrap">
                                                                                        {
                                                                                            filterItem.label
                                                                                        }
                                                                                    </Box>
                                                                                </Checkbox>
                                                                            );
                                                                        }
                                                                    )}
                                                                </VStack>
                                                            </>
                                                        ) : (
                                                            t(
                                                                'ui.mm.filter.select-one-description'
                                                            )
                                                        )}
                                                    </Flex>
                                                ) : (
                                                    t('ui.mm.filter.no-options')
                                                )}
                                            </Flex>
                                        </Flex>
                                    </Flex>
                                </Flex>
                            );
                        })}
                        {allowMultipleFilters && (
                            <CanopyButton
                                onClick={handleAddFilter}
                                data-test="add-filter"
                                alignSelf="flex-start"
                                pt="7px"
                                fontSize="sm"
                                variant="ghost"
                                mt="20px"
                                pb="7px"
                                mb="20px"
                                isDisabled={isDisabledAddButton()}>
                                <AddIcon
                                    width={'12px !important'}
                                    height={'12px !important'}
                                    mr="4px"
                                />
                                {t('ui.mm.filters.add-filter')}
                            </CanopyButton>
                        )}
                    </Flex>

                    <Flex
                        position="absolute"
                        bottom="0"
                        direction="column"
                        marginBottom="6px"
                        width="100%">
                        <Flex
                            direction="column"
                            width="100%">
                            <Divider
                                borderColor={currentColors.brand['700']}
                                width="100%"
                                my="10px"
                            />
                            <Flex>
                                <CanopyButton
                                    width="154px"
                                    fontSize="sm"
                                    variant="outline"
                                    onClick={handleResetFilter}
                                    disabled={
                                        localSelectedGroupBy[0] === 'select-one'
                                    }
                                    mr="11.5px">
                                    {t('reset')}
                                </CanopyButton>
                                <CanopyButton
                                    isDisabled={isDisabledApplyButton()}
                                    width="154px"
                                    height="35px"
                                    fontSize="sm"
                                    onClick={handleApplyFilter}>
                                    {t('apply-filter')}
                                </CanopyButton>
                            </Flex>
                        </Flex>
                    </Flex>
                </DrawerContent>
            </Drawer>
        </>
    );
}
export default Filters;
